/**
 * The second recovery step: the paragraph on either side of a remembered
 * neighbour, most recent neighbour first. When a paragraph is deleted and
 * retyped it gets a new `uniqueLocalId`, but the paragraphs around it keep
 * theirs, so the redline's text is looked for next to them.
 */
import type { CandidateScorer } from "./candidate-scorer";
import type { RecoveryContext, RecoveryStep } from "./recovery-chain";
import { RecoveryResult } from "./recovery-result";

export class NeighbourIdStep implements RecoveryStep {
    readonly tier = "neighbour-id";
    readonly triggers = "any";

    constructor(private readonly scorer: CandidateScorer) {}

    async recover({ body, memory }: RecoveryContext): Promise<RecoveryResult> {
        for (const neighbour of [...memory.neighbours].reverse()) {
            const anchor = body.paragraphById(neighbour.uniqueLocalId);
            /**
             * WHEN the remembered neighbour paragraph is gone
             *   CONSEQUENCE
             *     Skip this neighbour and try the next remembered anchor.
             */
            if (!anchor) continue;
            const scored = body
                .neighboursOf(anchor)
                .map((candidate) => ({ candidate, score: this.scorer.similarity(candidate.text, memory.lastSpanText) }))
                .sort((left, right) => right.score - left.score);
            const acceptedCandidates = scored.filter(({ score }) => this.scorer.accepts(score));
            /**
             * CASES
             *   - no candidate is accepted         -> keep checking other remembered neighbours
             *   - the top two accepted are tied    -> ambiguous; the step names no paragraph
             *   - one clear accepted leader        -> recover on it and return
             *
             * EXAMPLE
             *   After sorting by score descending:
             *     scores   = [0.91, 0.74, 0.40], candidateSimilarity 0.75
             *     accepted -> [0.91], one entry, no tie to test -> recover on it
             */
            if (acceptedCandidates.length === 0) continue;
            const enoughAcceptedCandidatesForTieCheck = acceptedCandidates.length > 1;
            /**
             * WHEN the two best accepted neighbours are closer than `candidateMargin`
             *   CONSEQUENCE
             *     Report ambiguity rather than naming one. `RecoveryBody.neighboursOf` answers
             *     [next, previous] and the sort is stable, so taking index 0 on a tie would
             *     hand the redline to the paragraph AFTER the remembered neighbour — which
             *     on a contract holding the same clause twice is the wrong one as often as
             *     the right one, with `outcome` still reading "recovered".
             *
             * WHY
             *   `ChangeLedgerStep` makes the same check for the same reason: a guess here
             *   re-anchors the redline on a paragraph the user never redlined, and this step
             *   runs FIRST, so its guess pre-empts the tier that would have caught it.
             */
            if (enoughAcceptedCandidatesForTieCheck && this.scorer.tied(acceptedCandidates[0].score, acceptedCandidates[1].score)) {
                return RecoveryResult.ambiguous("two neighbouring paragraphs match the redline too closely", []);
            }
            return RecoveryResult.recoveredOnParagraph([acceptedCandidates[0].candidate]);
        }
        return RecoveryResult.notFound(null);
    }
}
